import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import EnterpriseIcon, { CaretsVerticalIcon } from '@enterprise-ui/icons';
import classnames from 'classnames';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import alertUserOfDeprecatedProps from '../../utils/deprecation.js';
import uuid from '../../utils/uuid.js';

var _excluded = ["id", "className", "containerOptions", "defaultValue", "disabled", "error", "highlight", "onChange", "onUpdate", "options", "optionsProps", "value"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

// SELECT
var InputSelect = function InputSelect(props) {
  var idRef = uuid();
  var _id$props = _objectSpread({
      id: idRef.current
    }, props),
    id = _id$props.id,
    className = _id$props.className,
    containerOptions = _id$props.containerOptions,
    defaultValue = _id$props.defaultValue,
    disabled = _id$props.disabled,
    error = _id$props.error,
    highlight = _id$props.highlight,
    onChange = _id$props.onChange,
    onUpdate = _id$props.onUpdate,
    _id$props$options = _id$props.options,
    options = _id$props$options === void 0 ? [] : _id$props$options,
    optionsProps = _id$props.optionsProps,
    value = _id$props.value,
    otherProps = _objectWithoutProperties(_id$props, _excluded);
  alertUserOfDeprecatedProps('InputSelect', otherProps);
  if (!id) {
    console.error("Hey Canvas User! A unique id is required for the Select input.");
  }

  // The user-selected value
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    userValue = _useState2[0],
    setUserValue = _useState2[1];
  var prevUserValueRef = useRef();
  useEffect(function () {
    prevUserValueRef.current = userValue;
  });
  /*
   The user's previously selected value
   - Evaluates before useEffect @ line #38 to store the previous value
    Alert: Use `prevUserValue` -not- `prevUserValueRef.current`
  */
  var prevUserValue = prevUserValueRef.current;

  // Option list values
  var values = useMemo(function () {
    return options.map(function (o) {
      return o.value;
    });
  }, [options]);
  var _useState3 = useState(typeof value !== 'undefined' ? values.indexOf(value) : values.indexOf(defaultValue)),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedValueIndex = _useState4[0],
    setSelectedValueIndex = _useState4[1];

  // React value and options updates
  useEffect(function () {
    // If value is supplied, then we are a controlled component.
    // If we're a controlled component then the state must be whatever the supplied value is
    if (typeof value !== 'undefined') {
      setSelectedValueIndex(values.indexOf(value));
    }
  }, [value, selectedValueIndex, values]);

  // React to defaultValue and options updates
  useEffect(function () {
    // If no value is supplied, we are an uncontrolled component
    // then the state must be whatever the defaultValue is, unless a user previously selected something
    if (typeof value === 'undefined' && typeof defaultValue !== 'undefined') {
      /*
       - The user did not select a value before the options changed
         - Use the defaultValue to set the selected index
      */
      if (prevUserValue === null && values.indexOf(defaultValue) > -1) {
        setSelectedValueIndex(values.indexOf(defaultValue));
      }
      if (prevUserValue) {
        /*
         - The user selected a value and then the options changed
         - Do the new values contain the old value?
           - yes: use that index
           - no: use the defaultValue to set the selected index
        */

        if (values.indexOf(prevUserValue) > -1) {
          setSelectedValueIndex(values.indexOf(prevUserValue));
        } else {
          setSelectedValueIndex(values.indexOf(defaultValue));
        }
      }
    }
  }, [values, defaultValue, prevUserValue, value]);
  var handleOnChange = function handleOnChange(event) {
    var index = parseInt(event.target.value);
    setSelectedValueIndex(index);
    var actualValue = values[index];
    setUserValue(actualValue);
    onUpdate && onUpdate(otherProps.name || id, actualValue);
    onChange && onChange(event);
  };
  var finalClasses = classnames('C-Input C-Select', {
    isDisabled: disabled,
    isErrored: error,
    isHighlighted: highlight
  }, className);
  var showNonSelected = selectedValueIndex === -1;
  return /*#__PURE__*/React.createElement("span", _extends({
    className: "C-Input__Parent"
  }, containerOptions), /*#__PURE__*/React.createElement("select", _extends({
    className: finalClasses,
    id: id,
    disabled: disabled,
    value: selectedValueIndex,
    onChange: handleOnChange
  }, otherProps), showNonSelected && /*#__PURE__*/React.createElement("option", {
    hidden: true
  }), options && options.map(function (option, index) {
    return /*#__PURE__*/React.createElement(InputSelectOption, _extends({
      key: "".concat(id, "_").concat(index),
      id: "".concat(id, "_").concat(index),
      disabled: option.disabled,
      value: index
    }, optionsProps), option.label);
  })), /*#__PURE__*/React.createElement(EnterpriseIcon, {
    className: disabled ? 'isDisabled C-Select__Icon' : 'C-Select__Icon',
    icon: CaretsVerticalIcon
  }));
};
InputSelect.displayName = 'Input.Select';

// SELECT OPTION
var InputSelectOption = function InputSelectOption(props) {
  return /*#__PURE__*/React.createElement("option", props);
};
InputSelectOption.displayName = 'Input.SelectOption';

export { InputSelectOption, InputSelect as default };
